import { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Users, Plus, Mail, Phone, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const getBackendUrl = () => {
  if (window.location.hostname !== 'localhost') {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};

const BACKEND_URL = getBackendUrl();

function Contacts() {
  const [loading, setLoading] = useState(true);
  const [contacts, setContacts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const companiesRes = await fetch(`${BACKEND_URL}/api/companies`, {
        credentials: 'include'
      });
      
      if (companiesRes.ok) {
        const companiesData = await companiesRes.json();
        setCompanies(companiesData);
        
        if (companiesData.length > 0) {
          const contactsRes = await fetch(`${BACKEND_URL}/api/contacts/${companiesData[0].company_id}`, {
            credentials: 'include'
          });
          
          if (contactsRes.ok) {
            const contactsData = await contactsRes.json();
            setContacts(contactsData);
          }
        }
      }
    } catch (error) {
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (companies.length === 0) {
      toast.error('Please create a company first');
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/contacts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          company_id: companies[0].company_id
        })
      });
      
      if (response.ok) {
        toast.success('Contact added successfully');
        setDialogOpen(false);
        setFormData({ name: '', email: '', phone: '', role: '' });
        fetchData();
      } else {
        throw new Error('Failed to add contact');
      }
    } catch (error) {
      toast.error('Failed to add contact');
    }
  };

  const handleDelete = async (contactId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/contacts/${contactId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Contact deleted successfully');
        fetchData();
      } else {
        throw new Error('Failed to delete contact');
      }
    } catch (error) {
      toast.error('Failed to delete contact');
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Contacts">
        <div className="flex items-center justify-center h-64">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Contacts">
      <div className="space-y-6" data-testid="contacts-page">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Users className="h-8 w-8 text-primary" />
            <h2 className="text-2xl font-mono font-bold">Team Contacts</h2>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                className="bg-primary text-primary-foreground hover:bg-primary/90 font-mono"
                data-testid="add-contact-button"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Contact
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border">
              <DialogHeader>
                <DialogTitle className="font-mono">Add New Contact</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="add-contact-form">
                <div>
                  <Label htmlFor="name" className="font-mono">Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="font-mono mt-1"
                    data-testid="contact-name-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="email" className="font-mono">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="font-mono mt-1"
                    data-testid="contact-email-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="phone" className="font-mono">Phone</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="font-mono mt-1"
                    data-testid="contact-phone-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="role" className="font-mono">Role</Label>
                  <Input
                    id="role"
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="font-mono mt-1"
                    placeholder="e.g., IT Manager"
                    data-testid="contact-role-input"
                  />
                </div>
                
                <Button type="submit" className="w-full font-mono" data-testid="submit-contact-button">
                  Add Contact
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {contacts.length === 0 ? (
          <div className="bg-card border border-border rounded-lg p-12 text-center" data-testid="no-contacts-message">
            <Users className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-mono font-bold mb-2">No Contacts Yet</h3>
            <p className="text-muted-foreground">Add your first team contact to get started</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {contacts.map((contact) => (
              <div key={contact.contact_id} className="bg-card border border-border rounded-lg p-6" data-testid={`contact-card-${contact.contact_id}`}>
                <div className="flex justify-between items-start mb-4">
                  <div className="h-12 w-12 rounded-full bg-primary/20 flex items-center justify-center">
                    <span className="text-primary font-mono font-bold text-lg">{contact.name.charAt(0)}</span>
                  </div>
                  <button
                    onClick={() => handleDelete(contact.contact_id)}
                    className="text-muted-foreground hover:text-destructive"
                    data-testid={`delete-contact-${contact.contact_id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                
                <h3 className="font-mono font-bold mb-1" data-testid={`contact-name-${contact.contact_id}`}>{contact.name}</h3>
                {contact.role && (
                  <p className="text-sm text-muted-foreground font-mono mb-3">{contact.role}</p>
                )}
                
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="h-4 w-4 text-primary" />
                    <span className="font-mono text-xs" data-testid={`contact-email-${contact.contact_id}`}>{contact.email}</span>
                  </div>
                  {contact.phone && (
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="h-4 w-4 text-primary" />
                      <span className="font-mono text-xs">{contact.phone}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export default Contacts;